// GRN phase 1 (2026-10-21): Goods Receiving opens on what is due. Vendor orders created after go-live only
// (g13 start clean): older POs stay on their own pages, exactly as before.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Empty, Spin, Tag, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ExpectedDeliveries as Data, ExpectedDeliveryRow, getExpectedDeliveries, getPO } from '../../api';
import ResponsiveTable from '../responsive/ResponsiveTable';
import ReceiveWindow from './ReceiveWindow';
import { money } from './shared';

const STATE: Record<ExpectedDeliveryRow['state'], { label: (r: ExpectedDeliveryRow) => string; color: string }> = {
  late: { label: (r) => `Late ${r.lateDays} day${r.lateDays === 1 ? '' : 's'}`, color: 'red' },
  due_today: { label: () => 'Due today', color: 'orange' },
  this_week: { label: () => 'Due this week', color: 'blue' },
  later: { label: () => 'Later', color: 'default' },
  stale: { label: (r) => `No delivery for ${r.quietDays} days`, color: 'volcano' },
};

const ExpectedDeliveries: React.FC<{ canReceive: boolean }> = ({ canReceive }) => {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [receivePo, setReceivePo] = useState<any>(null);
  const [opening, setOpening] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getExpectedDeliveries()); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not load expected deliveries'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReceive = async (poId: string) => {
    setOpening(poId);
    try { setReceivePo(await getPO(poId)); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not open the PO'); } finally { setOpening(null); }
  };

  if (loading && !data) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
  const c = data?.counts;
  const tile = (n: number, label: string, sub?: string, tone?: string) => (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: '10px 12px', background: '#fff', minWidth: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: n > 0 ? tone : undefined }}>{n}</div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: '#5f6672' }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {c && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {tile(c.dueToday, 'Due today', undefined, '#d46b08')}
          {tile(c.late, 'Late', c.late ? `oldest ${c.lateOldestDays} day${c.lateOldestDays === 1 ? '' : 's'}` : undefined, '#cf1322')}
          {tile(c.thisWeek, 'Due this week', undefined, '#0958d9')}
          {tile(c.stale, 'Partly delivered, no news 14+ days', c.stale ? `EGP ${money(c.staleValue, 0)} open` : undefined, '#d4380d')}
          {tile(c.replacements, 'Replacements due', undefined, '#d46b08')}
        </div>
      )}
      <ResponsiveTable<ExpectedDeliveryRow>
        rowKey="id"
        loading={loading}
        dataSource={data?.rows || []}
        pagination={false}
        size="small"
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No vendor orders waiting. POs created as “Order from vendor” appear here." /> }}
        card={{ header: ['po', 'state'], footer: ['go'] }}
        columns={[
          { title: 'PO', key: 'po', width: 150, render: (_: any, r) => <span><Link to={`/pos/${r.id}`}><code>{r.poId}</code></Link><div style={{ fontSize: 12, color: '#5f6672' }}>{r.mrIds.join(', ')}</div></span> },
          { title: 'Vendor', key: 'vendor', width: 170, render: (_: any, r) => <Link to={`/vendors/${r.vendor.id}`}>{r.vendor.name}</Link> },
          { title: 'Expected', key: 'expected', width: 120, render: (_: any, r) => (r.expected ? dayjs(r.expected).format('D MMM') : '—') },
          {
            title: 'Open lines', key: 'lines',
            render: (_: any, r) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {r.openLines.map((l) => (
                  <span key={l.id} style={{ fontSize: 13 }}>
                    <bdi>{l.item}</bdi> · <b>{l.open}</b>{l.open !== l.qty ? ` of ${l.qty}` : ''} {l.unit || ''}{l.pendingCancel ? <Tag style={{ marginLeft: 6 }}>waiting for Ahmed</Tag> : null}
                  </span>
                ))}
              </div>
            ),
          },
          { title: 'Open value', key: 'value', width: 120, align: 'right' as const, render: (_: any, r) => money(r.openValue, 0) },
          { title: 'State', key: 'state', width: 170, render: (_: any, r) => <span><Tag color={STATE[r.state].color}>{STATE[r.state].label(r)}</Tag>{r.replacement && <Tag color="orange">replacement</Tag>}</span> },
          ...(canReceive ? [{ title: '', key: 'go', width: 120, render: (_: any, r: ExpectedDeliveryRow) => <Button type="primary" size="small" icon={<InboxOutlined />} loading={opening === r.poId} onClick={() => openReceive(r.poId)}>Receive</Button> }] : []),
        ]}
      />
      <div style={{ fontSize: 12, color: '#5f6672' }}>
        Vendor orders created from now on. Older POs, their balances and rejections stay on their own pages as before.
      </div>
      {receivePo && (
        <ReceiveWindow open={!!receivePo} po={receivePo} onClose={() => setReceivePo(null)} onSaved={() => { setReceivePo(null); load(); }} />
      )}
    </div>
  );
};

export default ExpectedDeliveries;
